import React from 'react';
import { Info } from 'lucide-react';
import { useOptimizedKeywordTracking } from '../../pages/NewKeywordTrackingUI/OptimizedKeywordTrackingUIContainer';

const KeywordinteligenceCards = () => {
  const { filteredSearchResults, pageDetails, openUpgradeModal } = useOptimizedKeywordTracking();

  const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%"

  // Calculate actual data from filtered results
  const totalApps = pageDetails?.total_count || 0;
  // Detect placeholder presence and sum numeric revenues safely
  const hasLockedRevenue = (filteredSearchResults || []).some(app => (app && (app.revenue === LOCK_PLACEHOLDER || app?.stats?.revenue === LOCK_PLACEHOLDER)))
  const totalRevenue = (filteredSearchResults || []).reduce((sum, app) => {
    if (!app) return sum
    const r = app.revenue ?? app?.stats?.revenue
    if (r == null) return sum
    if (typeof r === 'number') return sum + (isFinite(r) ? r : 0)
    if (typeof r === 'string') {
      if (r === LOCK_PLACEHOLDER) return sum
      const n = Number(String(r).replace(/[,\s]/g, ''))
      return isFinite(n) ? sum + n : sum
    }
    return sum
  }, 0)
  const totalPopularity = 'NA';
  // Downloads: detect placeholder-only locking and sum numeric downloads
  const hasLockedDownloads = (filteredSearchResults || []).some(app => (app && (app.downloads === LOCK_PLACEHOLDER || app?.stats?.downloads === LOCK_PLACEHOLDER)))
  const totalDownloads = (filteredSearchResults || []).reduce((sum, app) => {
    if (!app) return sum
    const d = app.downloads ?? app?.stats?.downloads
    if (d == null) return sum
    if (typeof d === 'number') return sum + (isFinite(d) ? d : 0)
    if (typeof d === 'string') {
      if (d === LOCK_PLACEHOLDER) return sum
      const n = Number(String(d).replace(/[,\s]/g, ''))
      return isFinite(n) ? sum + n : sum
    }
    return sum
  }, 0)

  // How many items are currently shown in the results (used for the 'Showing Top X Apps' label)
  const shownCount = (filteredSearchResults || []).length || totalApps;

  // Demo data for changes (replace with real data as needed)
  const popularityChange = '+5%';
  const revenueChange = '+7%';

  // Function to format revenue with appropriate suffix
  const formatRevenue = (revenue) => {
    if (revenue >= 1000000) {
      return `$${Math.round(revenue / 1000000)}M`;
    } else if (revenue >= 1000) {
      return `$${Math.round(revenue / 1000)}K`;
    } else {
      return `$${Math.round(revenue)}`;
    }
  };

  const formatDownloads = (n) => {
    const num = Number(n) || 0
    if (num >= 1e9) return Math.round(num / 1e9) + 'B'
    if (num >= 1e6) return Math.round(num / 1e6) + 'M'
    if (num >= 1e3) return Math.round(num / 1e3) + 'K'
    return String(Math.round(num))
  }

  return (
    <div className="keyword-intel-root">
      <h3 className="keyword-intel-title">Top Card
        <span className="keyword-intel-info" title="Showing Total downloads,Total revenue and Total apps against the searched keyword." role="img" aria-label="info" data-testid="keyword-intel-info">
          <Info size={16} />
        </span>
      </h3>
      <div className=" row gx-5 row-cols-md-3 gap-md-0 gap-4">
        <div className='col-12 col-md-4'>
          {/* Popularity Card */}
          <div className="keyword-intel-card d-flex justify-content-between flex-column">
            <div className='d-flex align-items-center justify-content-between w-100'>
              <div className="keyword-intel-icon-wrap">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M38.8031 1.63401C38.6375 1.68913 38.4248 1.8397 38.3307 1.9686C38.2365 2.09751 37.8164 3.11001 37.3971 4.2186C36.9777 5.3272 36.5996 6.27201 36.5569 6.31813C36.5141 6.36426 35.4521 6.45295 34.1968 6.5152C32.8929 6.57979 31.8288 6.6742 31.7145 6.73541C31.2288 6.99529 30.9826 7.67601 31.1842 8.20138C31.2169 8.28632 32.0832 9.0321 33.1096 9.8587C34.6927 11.1336 34.9664 11.3908 34.9146 11.5541C34.6829 12.2841 33.7506 15.8326 33.7507 15.9842C33.7508 16.2426 34.0717 16.7459 34.3216 16.8797C34.8429 17.1587 35.1402 17.0399 37.0924 15.7724C38.1158 15.1079 39.0072 14.5323 39.0733 14.4933C39.1493 14.4487 39.9195 14.8969 41.1611 15.7083C42.8172 16.7907 43.1851 16.9942 43.4856 16.9942C44.0926 16.9942 44.5625 16.541 44.5625 15.9558C44.5625 15.8108 44.2955 14.7129 43.9691 13.516C43.4296 11.5374 43.3897 11.3293 43.5292 11.2244C44.6468 10.3854 46.9888 8.42601 47.1021 8.23541C47.1834 8.09854 47.2498 7.86651 47.2496 7.71979C47.2493 7.39223 46.957 6.89404 46.6775 6.74451C46.556 6.67945 45.6892 6.5976 44.5866 6.54688C43.5506 6.49935 42.4902 6.43916 42.23 6.41329L41.7567 6.36613L40.9643 4.25113C40.1269 2.01585 39.9123 1.65248 39.3843 1.57495C39.2303 1.55235 38.9687 1.57898 38.8031 1.63401ZM10.6279 2.8222C10.2604 3.01129 10.2188 3.28663 10.2188 5.52716C10.2188 7.33841 10.1923 7.75551 10.0478 8.21807C9.69347 9.35338 8.93409 10.1956 7.03978 11.5545L5.89622 12.3749H3.55322H1.21022L0.980156 12.605L0.75 12.8351V19.1249V25.4146L0.980156 25.6447L1.21022 25.8749H3.51994H5.82956L6.18553 26.1795C7.31428 27.1456 8.67975 27.648 10.3869 27.7253C11.6214 27.7812 13.964 27.5761 15.0938 27.3132C15.5468 27.2078 16.2143 27.1743 17.6961 27.1828C18.7921 27.189 19.8474 27.1515 20.0412 27.0993C20.6627 26.932 21.2489 26.4873 21.5554 25.9507C21.9379 25.2809 22.0064 24.7593 21.8106 24.0089C21.7244 23.6787 21.665 23.4028 21.6785 23.3957C22.6383 22.8969 23.1705 21.5592 22.8368 20.4842C22.7568 20.2264 22.7299 20.0023 22.7771 19.9862C23.0865 19.8808 23.6184 19.3644 23.843 18.9515C24.3829 17.9583 24.2509 16.9034 23.4793 16.0453L23.0655 15.585L23.2243 15.269C23.3116 15.0952 23.4 14.6928 23.4207 14.3748C23.4829 13.4197 22.9227 12.4665 22.0414 12.0276C21.7374 11.8763 21.4035 11.8592 18.7489 11.8592H15.7946L15.9567 11.3905C16.1736 10.7636 16.1908 7.78063 15.9833 6.79673C15.6171 5.05991 14.3807 3.6456 12.6931 3.03304C11.993 2.77888 10.924 2.66976 10.6279 2.8222ZM35.9489 18.5113L35.7188 18.7413V32.3699V45.9984L35.9607 46.2257L36.2026 46.453H39.1925C42.1788 46.453 42.1825 46.4527 42.3958 46.2394L42.6094 46.0259L42.6353 32.5519C42.6497 25.1413 42.6386 18.9842 42.6108 18.8697C42.583 18.755 42.4684 18.5758 42.3562 18.4712C42.16 18.2885 42.0362 18.2811 39.1656 18.2811H36.179L35.9489 18.5113ZM25.944 23.5545C25.8592 23.619 25.7456 23.7905 25.6917 23.9356C25.5479 24.323 25.5561 45.5201 25.7002 45.9334C25.8849 46.4636 26.1023 46.4999 29.0924 46.4999C31.8355 46.4999 32.2627 46.4516 32.4395 46.1212C32.4979 46.0121 32.5312 41.9274 32.5312 34.8974V23.8451L32.3143 23.6412C32.1008 23.4406 32.0484 23.4374 29.0978 23.4374C26.8431 23.4374 26.0601 23.4664 25.944 23.5545ZM16.0647 29.1533C15.5385 29.3709 15.5625 28.9579 15.5625 37.7968V45.9269L15.7887 46.1899L16.015 46.453H19.0312H22.0475L22.2738 46.1899L22.5 45.9269V37.7811V29.6354L22.2739 29.3723L22.0477 29.1092L19.1567 29.0897C17.5666 29.079 16.1752 29.1076 16.0647 29.1533ZM5.71875 34.7811C5.53556 34.9643 5.53125 35.0936 5.53125 40.4592C5.53125 43.8613 5.56612 46.0149 5.62303 46.1212C5.79966 46.4512 6.22744 46.4999 8.95312 46.4999C11.6788 46.4999 12.1066 46.4512 12.2832 46.1212C12.3401 46.0149 12.375 43.8613 12.375 40.4592C12.375 35.0936 12.3707 34.9643 12.1875 34.7811C12.0077 34.6013 11.875 34.5936 8.95312 34.5936C6.03122 34.5936 5.89856 34.6013 5.71875 34.7811Z" fill="#FEB95A" />
                </svg>
              </div>
              <div className="keyword-intel-card-content">
                <div className="keyword-intel-card-label">Total Downloads</div>
                <div className="keyword-intel-card-value">
                  {hasLockedDownloads ? (
                    <span onClick={() => openUpgradeModal && openUpgradeModal()} role="button" title="Upgrade to view downloads" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)' }}>{formatDownloads(totalDownloads)}</span>
                      <button type="button" aria-label="Unlock detailed analytics" title="Unlock detailed analytics" style={{ background: 'transparent', border: 'none', padding: 0, margin: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); openUpgradeModal && openUpgradeModal(); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                          <path d="M17 8h-1V6a4 4 0 10-8 0v2H7a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V9a1 1 0 00-1-1zm-7-2a2 2 0 114 0v2h-4V6z" fill="#FFFFFF" />
                        </svg>
                      </button>
                    </span>
                  ) : (
                    formatDownloads(totalDownloads)
                  )}
                </div>

              </div>
            </div>
            <div>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-4'>
          {/* Total Revenue Card */}
          <div className="keyword-intel-card d-flex justify-content-between flex-column">
            <div className='d-flex align-items-center justify-content-between w-100'>
              <div className="keyword-intel-icon-wrap">
                <svg width="48" height="46" viewBox="0 0 48 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M17.3697 0.0621416C7.48387 1.10567 0.5775 10.1616 2.25056 19.8873C3.03366 24.4397 5.66794 28.5192 9.46875 31.0654C10.213 31.564 11.6293 32.3473 11.9668 32.447C12.1223 32.493 13.0359 31.9607 15.9981 30.0981C21.072 26.9077 20.9695 26.9691 21.5557 26.7675C22.3252 26.5027 23.1832 26.4545 23.9435 26.633C25.0936 26.9032 25.5824 27.2409 27.3923 29.0155C28.63 30.2291 29.0856 30.6213 29.1894 30.5628C29.589 30.3378 31.0478 28.9929 31.7037 28.2448C35.8638 23.5004 37.1021 16.9955 34.9597 11.1421C33.364 6.7828 29.8974 3.15252 25.5938 1.33395C24.3526 0.809423 23.2182 0.479235 21.8438 0.242235C20.6045 0.0284853 18.493 -0.0564522 17.3697 0.0621416ZM18.4688 7.1053C18.3399 7.15677 18.1356 7.2912 18.0145 7.40408C17.6108 7.78114 17.5312 8.05798 17.5312 9.08764V10.0595L16.9922 10.2294C13.9762 11.18 13.0398 14.9923 15.2738 17.2263C16.2478 18.2003 17.2472 18.5217 19.3233 18.5284C20.3156 18.5315 20.5788 18.645 20.8612 19.1912C21.1363 19.723 20.9758 20.378 20.4736 20.773C20.2362 20.9597 20.1156 20.9688 17.8702 20.9693C16.4503 20.9696 15.3685 21.0107 15.145 21.0727C13.8504 21.4321 13.6392 23.2029 14.8192 23.8045C15.0754 23.9352 15.3833 23.9685 16.3359 23.9686L17.5312 23.9688V24.917C17.5312 26.0372 17.6987 26.4426 18.3013 26.7813C18.9946 27.1709 19.9476 26.8901 20.3144 26.1879C20.4198 25.9863 20.4834 25.5694 20.5121 24.8922L20.5546 23.8938L20.826 23.8395C21.3739 23.7299 22.1857 23.2537 22.7136 22.7323C23.672 21.7856 24.1574 20.3249 23.9265 19.0829C23.7051 17.8922 22.9979 16.8079 22.0435 16.1954C21.2663 15.6965 20.8838 15.6058 19.2959 15.5427C17.6436 15.4772 17.4505 15.4065 17.1232 14.7477C16.8928 14.2843 16.9805 13.8368 17.3889 13.3909L17.7039 13.0469L20.3129 13C22.7121 12.9569 22.9482 12.9379 23.25 12.7635C23.6082 12.5565 24 11.8965 24 11.5C24 11.0362 23.6101 10.438 23.1598 10.211C22.7974 10.0282 22.5955 10 21.6487 10H20.5558L20.5127 9.02914C20.4776 8.2408 20.4306 7.99527 20.2625 7.7233C19.9097 7.15255 19.0732 6.86436 18.4688 7.1053ZM37.168 22.1763C36.511 22.4747 36 23.2426 36 23.9315C36 24.7395 36.1505 24.9721 37.679 26.5264L39.134 28.0059L35.0596 32.081L30.9852 36.1562L27.563 32.7434C25.6807 30.8664 23.9971 29.2589 23.8217 29.1712C23.5868 29.0538 23.3398 29.0218 22.8842 29.05L22.2656 29.0884L11.625 35.678C5.77266 39.3024 0.853219 42.3669 0.692906 42.4879C0.253969 42.8195 0.046875 43.3165 0.046875 44.0381C0.046875 44.5538 0.0890625 44.7181 0.305531 45.0451C0.861563 45.8855 1.8735 46.2169 2.72841 45.8386C2.95519 45.7384 7.5135 42.9492 12.8581 39.6407C18.2027 36.332 22.6317 33.625 22.7003 33.625C22.7689 33.625 24.4499 35.2459 26.4359 37.227C29.6738 40.4568 30.0901 40.8403 30.4648 40.9389C30.9333 41.0623 31.4678 41.0051 31.9083 40.7845C32.0705 40.7033 34.4062 38.4386 37.099 35.7518L41.9949 30.8669L43.474 32.3214C45.0279 33.8495 45.2605 34 46.0686 34C46.7574 34 47.5253 33.489 47.8237 32.832C47.9828 32.4817 47.9961 32.1183 47.9979 28.0205C48.0002 23.1871 48.0018 23.2031 47.456 22.621C47.3169 22.4727 47.0344 22.2732 46.8281 22.1778C46.4798 22.0164 46.137 22.0041 42 22.0041C37.8798 22.0041 37.5186 22.017 37.168 22.1763Z" fill="#A9DFD8" />
                </svg>
              </div>
              <div className="keyword-intel-card-content">
                <div className="keyword-intel-card-label">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', width: '100%' }}>
                    <span>Total Revenue</span>
                    <span
                      title="Add-ons revenue included in total"
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <Info size={14} style={{ opacity: 0.8, cursor: 'default' }} />
                    </span>
                  </span>
                </div>
                <div className="keyword-intel-card-value">
                  {hasLockedRevenue ? (
                    <span onClick={() => openUpgradeModal && openUpgradeModal()} role="button" title="Upgrade to view revenue" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)', opacity: 0.6, userSelect: 'none', pointerEvents: 'none' }}>{formatRevenue(totalRevenue)}</span>
                      <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff', cursor: 'pointer' }} />
                    </span>
                  ) : (
                    formatRevenue(totalRevenue)
                  )}
                </div>

              </div>
            </div>
            <div>
            </div>
          </div>
        </div>
        <div className='col-12 col-md-4'>
          {/* Total Apps Card */}
          <div className="keyword-intel-card">
            <div className="keyword-intel-icon-wrap">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 14.9999C1.10218 14.9999 0.720645 14.8419 0.43934 14.5606C0.158036 14.2793 0 13.8978 0 13.4999V1.50293C0 1.1051 0.158036 0.723574 0.43934 0.44227C0.720645 0.160965 1.10218 0.00292969 1.5 0.00292969H13.5C13.8978 0.00292969 14.2794 0.160965 14.5607 0.44227C14.842 0.723574 15 1.1051 15 1.50293V13.4999C15 13.8978 14.842 14.2793 14.5607 14.5606C14.2794 14.8419 13.8978 14.9999 13.5 14.9999H1.5ZM22.5 14.9999C22.1022 14.9999 21.7206 14.8419 21.4393 14.5606C21.158 14.2793 21 13.8978 21 13.4999V1.50293C21 1.1051 21.158 0.723574 21.4393 0.44227C21.7206 0.160965 22.1022 0.00292969 22.5 0.00292969H34.497C34.8948 0.00292969 35.2764 0.160965 35.5577 0.44227C35.839 0.723574 35.997 1.1051 35.997 1.50293V13.4999C35.997 13.8978 35.839 14.2793 35.5577 14.5606C35.2764 14.8419 34.8948 14.9999 34.497 14.9999H22.5ZM1.5 35.9999C1.10218 35.9999 0.720645 35.8419 0.43934 35.5606C0.158036 35.2793 0 34.8978 0 34.4999V22.4999C0 22.1021 0.158036 21.7206 0.43934 21.4393C0.720645 21.158 1.10218 20.9999 1.5 20.9999H13.5C13.8978 20.9999 14.2794 21.158 14.5607 21.4393C14.842 21.7206 15 22.1021 15 22.4999V34.4999C15 34.8978 14.842 35.2793 14.5607 35.5606C14.2794 35.8419 13.8978 35.9999 13.5 35.9999H1.5ZM22.5 35.9999C22.1022 35.9999 21.7206 35.8419 21.4393 35.5606C21.158 35.2793 21 34.8978 21 34.4999V22.4999C21 22.1021 21.158 21.7206 21.4393 21.4393C21.7206 21.158 22.1022 20.9999 22.5 20.9999H34.497C34.8948 20.9999 35.2764 21.158 35.5577 21.4393C35.839 21.7206 35.997 22.1021 35.997 22.4999V34.4999C35.997 34.8978 35.839 35.2793 35.5577 35.5606C35.2764 35.8419 34.8948 35.9999 34.497 35.9999H22.5Z" fill="#20AEF3" />
              </svg>
            </div>
            <div className="keyword-intel-card-content">
              <div className="keyword-intel-card-label">{`Showing Top`}</div>
              <div className="keyword-intel-card-value">{totalApps} Apps</div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .keyword-intel-root {
          width: 100%;
          margin-top: 12px;
        }
        .keyword-intel-title {
          color: #fff;
          margin: 24px 0;
          font-weight: 500;
          font-size: 24px;
          line-height: 22.5px;

        }
        .keyword-intel-cards-row {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
          justify-content: flex-start;
        }
        .keyword-intel-card {
          background: #21222D;
          border-radius: 10px;
          padding: 16px;
          // min-width: 240px;
          // max-width: 260px;
          flex: 1 1 240px;
          display: flex;
          align-items: flex-start;
          gap: 6px;
          box-shadow: none;
          transition: box-shadow 0.18s;
          height:100%;
        }
        .keyword-intel-card:not(:last-child) {
          margin-right: 0;
        }
        .keyword-intel-icon-wrap {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 0;
        }
        .keyword-intel-card-content {
          display: flex;
          flex-direction: column;
          gap: 0;
          flex: 1;
        }
        .keyword-intel-card-label {
          color: #b3b3c6;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 2px;
          letter-spacing: 0.01em;
          text-align:right;
        }
        .keyword-intel-card-value {
          color: #fff;
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 2px;
          margin-top: 0;
          letter-spacing: 0.01em;
          text-align:right;
        }
        .keyword-intel-info {
          display: inline-flex;
          align-items: center;
          margin-left: 8px;
          vertical-align: middle;
          cursor: help;
          color: #b3b3c6;
        }
        .keyword-intel-info svg {
          display: block;
        }
        .keyword-intel-card-change {
          font-size: 13px;
          font-weight: 600;
          margin-top: 2px;
          display: flex;
          align-items: center;
        }
        .keyword-intel-card-change-green {
          color: #4ade80;
          font-weight: 600;
          margin-right: 4px;
        }
        .keyword-intel-card-change-text {
          color: #b3b3c6;
          font-weight: 400;
        }
        @media (max-width: 900px) {
          .keyword-intel-cards-row {
            flex-direction: column !important;
            gap: 16px !important;
          }
          .keyword-intel-card {
            min-width: 0;
            width: 100%;
            max-width: 100%;
            min-height:120px;
          }
        }
      `}</style>
    </div>
  );
};

export default KeywordinteligenceCards;